// 请出去 and agent 上限: the page side of office/agentDoor.ts (artin: agents unlimited by default; the people in the
// office can ask an agent to leave, and can set a limit).
// - An agent's person card (greet.js) and its row in the online list (its menu) have 「请出去」: a short confirm
//   (「请 X 离开办公室 10 分钟？」), then {t:"agent_kick", id}. Everyone gets {t:"agent_kicked"} and a toast
//   「A 请 X 离开了办公室」; the agent's otter leaves (an ordinary "leave").
// - 设置 → agent 上限: 不限 or a number (0..LIMIT_MAX), sent as {t:"agent_limit", max}; the office tells everyone
//   {t:"agent_limit", max, by} and the welcome carries agentLimit / agentLimitBy. An agent refused for the limit never
//   gets an otter, so the page sees nothing of it.
// Who may: canManage() (any person now, as the office allows; never an agent or a guest). To keep it to admins later,
// change canManage() here and the check in office/agentDoor.ts together.
import { icon, label } from '/icons.js';
import { t } from '/i18n.js';

export const LIMIT_MAX = 50, KICK_MIN = 10;

const CSS = `.adpanel{display:flex;flex-direction:column;gap:6px}
.adpanel .seg{display:flex;flex-wrap:wrap;align-items:center;gap:6px}
.adpanel .seg button{font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 10px;background:#fff;cursor:pointer;white-space:nowrap}
.adpanel .seg button.sel{background:#FDD03B}
.adpanel .seg button:disabled{opacity:.45;cursor:default}
.adpanel input{font:inherit;font-size:16px;width:60px;padding:3px 6px;border:2px solid #1b1410;border-radius:10px;box-sizing:border-box}
#settings .adpanel .now{font-size:12px;color:#555}
#adConfirm{z-index:100} /* over the coach marks (tour.js, 95) */
#adConfirm .card{width:280px;max-width:calc(100vw - 32px);box-sizing:border-box}
#adConfirm h3{margin:0 0 6px;font-size:16px}
#adConfirm p{margin:0 0 12px;font-size:13px;color:#555}
#adConfirm .btns{display:flex;gap:8px}
#adConfirm .btns button{flex:1}
#adConfirm .btns .no{background:#fff}
#onlineList .admenu{border:0;background:none;padding:2px 8px;margin-right:-6px;cursor:pointer;flex:none;line-height:1}
#onlineList .adact{display:flex;justify-content:flex-end;padding:2px 10px 4px}
#onlineList .adact button{font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:2px 10px;background:#FDD03B;cursor:pointer}`;

/**
 * @param o {send, me, toast, addSettingsSection, changed}: changed() redraws the online list (its menu opened or closed).
 * @returns {{welcome(m), msg(m), canManage(), forCard(p), onlineRow(row, a)}}
 */
export function initAgentDoor(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  let limit = null, by = null, menuFor = null, repaint = null;

  /** Who may ask an agent to leave or set the limit: a member in the office, the same rule as the office's (an agent
   *  can't; a guest, 访客 (peer.guest / a "guest:" sub), is refused by office.ts GUEST_DENIED). */
  const canManage = () => { const me = o.me(); return !!me && me.kind === 'human' && !me.guest && !me.visitor && !String(me.sub ?? '').startsWith('guest:'); };
  const nowText = () => t(by ? 'app.agentDoor.nowBy' : 'app.agentDoor.now', { limit: limitText(limit), by });
  const limitText = (v) => (v == null ? t('app.agentDoor.limit.none') : v === 0 ? t('app.agentDoor.limit.zero') : t('app.agentDoor.limit.max', { n: v }));

  function confirmKick(p) {
    document.getElementById('adConfirm')?.remove();
    const d = document.createElement('div'); d.id = 'adConfirm'; d.className = 'overlay';
    const card = document.createElement('div'); card.className = 'card';
    const h = document.createElement('h3'); h.textContent = t('app.agentDoor.kickAsk', { name: p.name, min: KICK_MIN });
    const note = document.createElement('p'); note.textContent = t('app.agentDoor.kickNote', { min: KICK_MIN });
    const btns = document.createElement('div'); btns.className = 'btns';
    const no = document.createElement('button'); no.className = 'no'; no.textContent = t('app.agentDoor.cancel');
    const yes = label(document.createElement('button'), 'door', t('app.agentDoor.kick'));
    const close = () => { d.remove(); removeEventListener('keydown', esc, true); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); close(); } };
    no.onclick = close;
    yes.onclick = () => { close(); if (canManage()) o.send({ t: 'agent_kick', id: p.id }); };
    d.onclick = (e) => { if (e.target === d) close(); };
    addEventListener('keydown', esc, true);
    btns.append(no, yes); card.append(h, note, btns); d.append(card); document.body.append(d);
    yes.focus();
  }

  function panel(box) {
    box.replaceChildren();
    const wrap = document.createElement('div'); wrap.className = 'adpanel';
    const now = document.createElement('p'); now.className = 'now';
    now.textContent = nowText();
    if (!canManage()) { wrap.append(now); box.append(wrap); return; }
    const about = document.createElement('p'); about.textContent = t('app.agentDoor.about');
    const seg = document.createElement('div'); seg.className = 'seg';
    const none = document.createElement('button'); none.type = 'button'; none.textContent = t('app.agentDoor.pick.none');
    const some = document.createElement('button'); some.type = 'button'; some.textContent = t('app.agentDoor.pick.some');
    const n = document.createElement('input'); n.type = 'number'; n.min = '0'; n.max = String(LIMIT_MAX); n.step = '1'; n.inputMode = 'numeric';
    n.setAttribute('aria-label', t('app.agentDoor.title')); n.value = String(limit ?? 5);
    const unit = document.createElement('span'); unit.textContent = t('app.agentDoor.unit');
    const save = document.createElement('button'); save.type = 'button'; save.textContent = t('app.agentDoor.save');
    let pick = limit == null ? 'none' : 'some';
    // an empty box (or one the browser can't read: "-", "abc" give "") is no number, not 0 (0 keeps every agent out)
    const want = () => { if (pick === 'none') return null; if (!n.value.trim() || !n.validity.valid) return undefined; const v = Number(n.value); return Number.isInteger(v) && v >= 0 && v <= LIMIT_MAX ? v : undefined; };
    const paint = () => {
      none.classList.toggle('sel', pick === 'none'); some.classList.toggle('sel', pick === 'some'); n.disabled = pick === 'none';
      const w = want(); save.disabled = w === undefined || w === limit;
      save.title = w === undefined ? t('app.agentDoor.range', { max: LIMIT_MAX }) : '';
    };
    none.onclick = () => { pick = 'none'; paint(); };
    some.onclick = () => { pick = 'some'; paint(); n.focus(); };
    n.oninput = paint;
    n.onkeydown = (e) => { if (e.key === 'Enter' && !save.disabled) save.click(); };
    save.onclick = () => { const w = want(); if (w === undefined || !canManage()) return; o.send({ t: 'agent_limit', max: w }); save.disabled = true; };
    seg.append(none, some, n, unit, save);
    wrap.append(about, seg, now); box.append(wrap);
    paint(); repaint = paint;
  }
  let shownBox = null;
  o.addSettingsSection?.({ id: 'agent-door', title: () => t('app.agentDoor.title'), icon: 'robot', order: 52, render(box) { shownBox = box; panel(box); } });
  /** The limit changed (or the welcome): redraw the section if it's open, but while I'm editing it only its 现在 line
   *  (so a change from someone else doesn't throw away what I'm typing). */
  const redraw = () => {
    if (!shownBox?.isConnected) return;
    if (!shownBox.contains(document.activeElement)) return panel(shownBox);
    const now = shownBox.querySelector('.now'); if (now) now.textContent = nowText();
    repaint?.(); // 保存 off if what I typed is what it is now
  };

  return {
    canManage,
    welcome(m) { limit = Number.isInteger(m.agentLimit) ? m.agentLimit : null; by = m.agentLimitBy || null; redraw(); },
    msg(m) {
      if (m.t === 'agent_limit') {
        limit = Number.isInteger(m.max) ? m.max : null; by = m.by || null; redraw();
        if (m.by) o.toast(limit == null ? t('app.agentDoor.changed.none', { by: m.by }) : t('app.agentDoor.changed.max', { by: m.by, n: limit }), null, 'robot');
      } else if (m.t === 'agent_kicked') {
        if (menuFor === m.id) menuFor = null;
        o.toast(t('app.agentDoor.kicked', { by: m.by || t('app.agentDoor.someone'), name: m.name || 'agent' }), null, 'door');
      }
    },
    /** The 「请出去」 button on an agent's person card (greet.js), or null when I may not. */
    forCard(p) { return p.kind === 'agent' && canManage() ? { icon: 'door', label: t('app.agentDoor.kick'), go: () => confirmKick(p) } : null; },
    /** An agent's row in the online list (app.js showOnline): a menu button on it; when that menu is open, returns the
     *  line with 「请出去」 to put under the row (else null). */
    onlineRow(row, a) {
      if (a.kind !== 'agent' || !canManage()) return null;
      const b = document.createElement('button'); b.type = 'button'; b.className = 'admenu'; b.title = t('app.agentDoor.more'); b.setAttribute('aria-label', t('app.agentDoor.menu', { name: a.name }));
      b.setAttribute('aria-expanded', String(menuFor === a.id));
      b.append(icon(menuFor === a.id ? 'caret_up' : 'caret_down'));
      b.onclick = (e) => { e.stopPropagation(); menuFor = menuFor === a.id ? null : a.id; o.changed?.(); };
      row.append(b);
      if (menuFor !== a.id) return null;
      const act = document.createElement('div'); act.className = 'adact';
      const k = label(document.createElement('button'), 'door', t('app.agentDoor.kick')); k.type = 'button';
      k.onclick = (e) => { e.stopPropagation(); menuFor = null; o.changed?.(); confirmKick(a); };
      act.append(k);
      return act;
    },
  };
}
